import { useEffect, type RefObject } from 'react';

/**
 * Publishes a header's height as --header-height on the root element, so
 * what must stay clear of the header can offset by it: the dashboard's
 * sidebar and Messages page start beneath it, and the support chat panel
 * never grows up under it. Both headers call this, the dashboard's (fixed) and the
 * public one (sticky), and a page has only one of them. The variable
 * leaves with its header, so a page with no header never reads the last
 * page's height.
 */
export const useHeaderHeight = (headerRef: RefObject<HTMLElement | null>) => {
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const root = document.documentElement;

    const publish = () => {
      root.style.setProperty('--header-height', `${header.offsetHeight}px`);
    };
    publish();

    // Fires whenever the header's box changes, a window resize that
    // rewraps it included.
    const observer = new ResizeObserver(publish);
    observer.observe(header);

    return () => {
      observer.disconnect();
      root.style.removeProperty('--header-height');
    };
  }, [headerRef]);
};
